import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { TextField, MenuItem } from '@material-ui/core';
import { designConstants as design } from 'styles/designConstants';
import './styles.scss';

const useStyles = makeStyles({
  root: {
    borderRadius: '3px 3px 0px 0px',
    margin: '7px 0px',
    '& .MuiInputBase-input': {
      fontSize: '17px',
      lineHeight: '21px',
      borderBottom: `2px solid rgba(${design.color.$teal_base}, 1)`,
    },
    '& .MuiFilledInput-underline:before': {
      content: 'none',
    },
    '& .MuiFilledInput-underline:after': {
      content: 'none',
    },
    '& .MuiSelect-selectMenu': {
      paddingRight: '60px',
    },
  },
  label: {
    // requires some value at this level to trigger remaining styles
    width: 'auto',
    '&.MuiInputLabel-root': { fontSize: 17 },
    '&.MuiInputLabel-shrink': { color: `rgba(${design.color.$teal_base}, 1)` },
  },
  menuItem: {
    '&:hover': {
      backgroundColor: `rgba(${design.color.$teal_base}, 1)`,
      color: design.color.$white_full,
    },
    '&.MuiListItem-root.Mui-selected': {
      backgroundColor: `rgba(${design.color.$teal_base}, 1)`,
      color: design.color.$white_full,
    },
  },
});

const FormSelector = ({
  dispatchToState,
  label,
  name: selectorName,
  optionFields,
  testidProp,
  value,
  fullWidth = true,
  required = false,
  emptyDataHelper,
}: IFormSelectorField) => {
  const classes = useStyles();
  const generateSelectionOptions = (selectOptions: ISelectOptions[]) => {
    return selectOptions.map((option) => {
      const { name, id } = option;
      return (
        <MenuItem
          classes={{ root: classes.menuItem }}
          data-testid="selection-option"
          id={`selection-option-${name}`}
          key={id}
          value={id}
        >
          {name}
        </MenuItem>
      );
    });
  };

  const generateEmptyOption = (emptyDataText: string) => (
    <MenuItem disabled data-testid="selection-empty-option">
      {emptyDataText}
    </MenuItem>
  );

  const isEmptyList = emptyDataHelper && optionFields.length === 0;
  const selectionOptions = generateSelectionOptions(optionFields);
  const updateSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name: targetName, value: targetValue } = event.target;
    dispatchToState!({ name: targetName, value: targetValue });
  };

  return (
    <TextField
      className={classes.root}
      data-testid={`select-form-options-${selectorName}`}
      fullWidth={fullWidth}
      inputProps={{ 'data-testid': testidProp }}
      InputLabelProps={{ className: classes.label }}
      label={label}
      name={selectorName}
      select
      variant="filled"
      onChange={updateSelection}
      value={value || ''}
      required={required}
    >
      {isEmptyList
        ? generateEmptyOption(emptyDataHelper as string)
        : selectionOptions}
    </TextField>
  );
};

//* -----------------------------------------------
// Interfaces
//* -----------------------------------------------

interface IDispatchArgs {
  name: string;
  value: string;
}

interface IFormSelectorField {
  label: string;
  name: string;
  optionFields: ISelectOptions[];
  testidProp: string; // for input data-testid
  value: Nullable<string>; // initial value delivered from parent component
  dispatchToState?: ({ name, value }: IDispatchArgs) => void;
  fullWidth?: boolean;
  emptyDataHelper?: string;
  required?: boolean;
}

export default FormSelector;
